/**
 * PianoRoll Component
 * Interactive MIDI Piano Roll with Note Editing, Timeline Ruler, Playhead,
 * Fully Editable Loop Zone (drag start, drag end, drag entire loop span, sweep in ruler),
 * and Resizable Piano Keyboard & Pitch Note Height.
 */

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import {
  MidiNote,
  QuantizeGrid,
  TimeSignature,
  LoopRange,
  DetectedChord,
} from '../types/midi';
import { ChordTrack } from './ChordTrack';
import {
  getPitchName,
  getTicksPerBeat,
  getTicksPerMeasure,
  isBlackKey,
} from '../core/timing';
import { getGridTicks, snapTickToGrid } from '../core/quantize';
import { PianoKeys } from './PianoKeys';
import { AudioEngine } from '../core/audioEngine';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  Magnet,
  ChevronLeft,
  ChevronRight,
  GripVertical,
  Focus,
  MousePointer2,
  Eraser,
  Scissors,

  LocateFixed,
  BarChart2,
  Keyboard,
} from 'lucide-react';


interface PianoRollProps {
  notes: MidiNote[];
  selectedNoteIds: Set<string>;
  onSelectNote: (id: string, multi: boolean) => void;
  onClearSelection: () => void;
  onSelectAll: () => void;
  onAddNote: (pitch: number, tick: number, duration?: number) => void;
  onUpdateNotesLive?: (notes: MidiNote[]) => void;
  onCommitNotes?: (notes: MidiNote[]) => void;
  onMoveNotes: (ids: string[], deltaTicks: number, deltaPitch: number) => void;
  onResizeNotes: (ids: string[], deltaTicks: number) => void;
  onDeleteSelected: () => void;
  onDeleteNote?: (id: string) => void;
  onSplitNote?: (id: string, atTick: number) => void;
  currentTick: number;
  onSeek: (tick: number) => void;
  bpm: number;
  timeSignature: TimeSignature;
  ppq: number;
  grid: QuantizeGrid;
  loopRange: LoopRange;
  onUpdateLoopRange: (range: LoopRange) => void;
  audioEngine: AudioEngine;
  isPlaying?: boolean;
  autoScroll?: boolean;
  onToggleAutoScroll?: () => void;
  showVelocityLane?: boolean;
  onToggleVelocityLane?: () => void;
  showVirtualKeyboard?: boolean;
  onToggleVirtualKeyboard?: () => void;
  livePitches?: Set<number>;
  totalMeasures?: number;
  keyboardWidth?: number;
  onKeyboardWidthChange?: (w: number) => void;
  measureChords?: Map<number, DetectedChord[]>;
  onUpdateChord?: (measureIndex: number, newChordSymbol: string) => void;
  onScrollPositionChange?: (scrollLeft: number) => void;
}

export const PianoRoll: React.FC<PianoRollProps> = ({
  notes,
  selectedNoteIds,
  onSelectNote,
  onClearSelection,
  onSelectAll,
  onAddNote,
  onUpdateNotesLive,
  onCommitNotes,
  onMoveNotes,
  onResizeNotes,
  onDeleteSelected,
  onDeleteNote,
  onSplitNote,
  currentTick,
  onSeek,
  timeSignature,
  ppq,
  grid,
  loopRange,
  onUpdateLoopRange,
  audioEngine,
  isPlaying = false,
  autoScroll = true,
  onToggleAutoScroll,
  showVelocityLane = false,
  onToggleVelocityLane,
  showVirtualKeyboard = false,
  onToggleVirtualKeyboard,
  livePitches,
  totalMeasures = 32,
  keyboardWidth: controlledKeyboardWidth,
  onKeyboardWidthChange,
  measureChords,
  onUpdateChord,
  onScrollPositionChange,
}) => {
  // Pitch range (MIDI 24: C1 to MIDI 96: C7 covers standard piano playing range)
  const minPitch = 24; // C1
  const maxPitch = 96; // C7

  // Zoom & Dimensions
  const [pixelsPerQuarter, setPixelsPerQuarter] = useState<number>(64);
  const [pitchRowHeight, setPitchRowHeight] = useState<number>(18);
  const [internalKeyboardWidth, setInternalKeyboardWidth] = useState<number>(76);
  const effectiveKeyboardWidth = controlledKeyboardWidth ?? internalKeyboardWidth;

  const pixelsPerTick = pixelsPerQuarter / ppq;
  const measureTicks = getTicksPerMeasure(timeSignature, ppq);
  const beatTicks = getTicksPerBeat(ppq, timeSignature.beatType);
  const gridTicks = getGridTicks(grid, ppq);

  // Dynamic measures count based on furthest note + padding
  const maxNoteEndTick = useMemo(() => {
    return notes.reduce((max, n) => Math.max(max, n.startTick + n.durationTicks), 0);
  }, [notes]);

  const effectiveMeasures = Math.max(
    totalMeasures,
    Math.ceil(maxNoteEndTick / measureTicks) + 4
  );

  const totalTicks = effectiveMeasures * measureTicks;
  const gridWidth = totalTicks * pixelsPerTick;
  const gridHeight = (maxPitch - minPitch + 1) * pitchRowHeight;

  // Container scroll ref
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Center scroll initially around Middle C (pitch 60)
  useEffect(() => {
    if (scrollContainerRef.current) {
      const middleCY = (maxPitch - 60) * pitchRowHeight;
      scrollContainerRef.current.scrollTop = middleCY - 160;
    }
  }, [pitchRowHeight, maxPitch]);

  // Snap State
  const [snapEnabled, setSnapEnabled] = useState<boolean>(true);
  const [snapGrid, setSnapGrid] = useState<QuantizeGrid | 'Auto'>('Auto');

  // Compute active effective snap grid
  const effectiveSnapGrid: QuantizeGrid = useMemo(() => {
    if (!snapEnabled) return 'Off';
    if (snapGrid === 'Auto') {
      return grid === 'Off' ? '1/16' : grid;
    }
    return snapGrid;
  }, [snapEnabled, snapGrid, grid]);

  // Stable notes ref for event callbacks
  const notesRef = useRef<MidiNote[]>(notes);
  notesRef.current = notes;

  // Last auditioned pitch to prevent audio spam while dragging vertical pitch
  const lastAuditionPitchRef = useRef<number | null>(null);

  // Dragging states with snapshots of starting positions
  interface NoteSnapshot {
    id: string;
    startTick: number;
    durationTicks: number;
    pitch: number;
    velocity: number;
  }

  // Tracking Space bar for hand/pan tool
  const [isSpacePressed, setIsSpacePressed] = useState<boolean>(false);

  // Active editing tool — select (default): click/drag notes, resize, marquee;
  // eraser: click/drag over notes deletes them; razor: click a note splits it in two at that point.
  const [toolMode, setToolMode] = useState<'select' | 'eraser' | 'razor'>('select');
  // Tracks whether the mouse button is held down while the eraser tool is active, so dragging
  // across multiple notes deletes each one it passes over (not just the first one clicked).
  const isErasingRef = useRef<boolean>(false);

  const [dragAction, setDragAction] = useState<{
    type: 'move' | 'resize-left' | 'resize-right' | 'marquee' | 'pan';
    originClientX: number;
    originClientY: number;
    primaryNoteId: string;
    targetIds: string[];
    snapshots: Map<string, NoteSnapshot>;
    hasMoved: boolean;
    // Marquee / Pan bounds
    startX?: number;
    startY?: number;
    currentX?: number;
    currentY?: number;
  } | null>(null);

  // Focus & Center View on notes or Middle C
  const handleCenterView = useCallback(() => {
    if (!scrollContainerRef.current) return;
    if (notes.length === 0) {
      const middleCY = (maxPitch - 60) * pitchRowHeight;
      scrollContainerRef.current.scrollTo({
        top: Math.max(0, middleCY - 160),
        behavior: 'smooth',
      });
      return;
    }
    const avgPitch = notes.reduce((sum, n) => sum + n.pitch, 0) / notes.length;
    const targetY = (maxPitch - avgPitch) * pitchRowHeight - (scrollContainerRef.current.clientHeight / 2);
    const minStartTick = notes.reduce((min, n) => Math.min(min, n.startTick), Infinity);
    const targetX = Math.max(0, minStartTick * pixelsPerTick - 48);

    scrollContainerRef.current.scrollTo({
      top: Math.max(0, targetY),
      left: targetX,
      behavior: 'smooth',
    });
  }, [notes, maxPitch, pitchRowHeight, pixelsPerTick]);

  // Keyboard Shortcuts (Delete, Backspace, Select All, Snap toggle S, Space for pan)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeTag = (document.activeElement?.tagName || '').toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select') {
        return;
      }

      if (e.code === 'Space' && !e.repeat) {
        setIsSpacePressed(true);
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        onDeleteSelected();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        onSelectAll();
      } else if (e.key.toLowerCase() === 's' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setSnapEnabled((prev) => !prev);
      } else if (e.key.toLowerCase() === 'f' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (onToggleAutoScroll) {
          e.preventDefault();
          onToggleAutoScroll();
        }
      } else if (e.key.toLowerCase() === 'v' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (onToggleVelocityLane) {
          e.preventDefault();
          onToggleVelocityLane();
        }
      } else if (e.key === '1' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setToolMode('select');
      } else if (e.key === '2' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setToolMode('eraser');
      } else if (e.key === '3' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setToolMode('razor');
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setIsSpacePressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [onDeleteSelected, onSelectAll, onToggleAutoScroll, onToggleVelocityLane]);

  // Eraser tool: stop drag-erasing wherever the mouse button is released, even outside a note.
  useEffect(() => {
    const stopErasing = () => { isErasingRef.current = false; };
    window.addEventListener('mouseup', stopErasing);
    return () => window.removeEventListener('mouseup', stopErasing);
  }, []);

  // ==========================================
  // Auto-scroll playhead during playback
  // ==========================================
  const lastPlayheadScrollTimeRef = useRef<number>(0);
  useEffect(() => {
    if (!autoScroll || !isPlaying || !scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const visibleWidth = container.clientWidth - effectiveKeyboardWidth;
    if (visibleWidth <= 80) return;

    const playheadX = currentTick * pixelsPerTick;
    const scrollLeft = container.scrollLeft;

    // Scroll if playhead goes past 75% of viewport or behind scrollLeft
    if (playheadX > scrollLeft + visibleWidth * 0.75 || playheadX < scrollLeft) {
      const now = Date.now();
      if (now - lastPlayheadScrollTimeRef.current > 120) {
        lastPlayheadScrollTimeRef.current = now;
        const targetScrollLeft = Math.max(0, playheadX - visibleWidth * 0.2);
        container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
      }
    }
  }, [autoScroll, isPlaying, currentTick, pixelsPerTick, effectiveKeyboardWidth]);

  // ==========================================
  // Resizing Piano Keyboard Width
  // ==========================================
  const [isResizingKeyboard, setIsResizingKeyboard] = useState<boolean>(false);
  const keyboardStartXRef = useRef<number>(0);
  const keyboardStartWidthRef = useRef<number>(effectiveKeyboardWidth);

  const handleKeyboardResizerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizingKeyboard(true);
    keyboardStartXRef.current = e.clientX;
    keyboardStartWidthRef.current = effectiveKeyboardWidth;
  };

  useEffect(() => {
    if (!isResizingKeyboard) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - keyboardStartXRef.current;
      const newWidth = Math.max(52, Math.min(180, keyboardStartWidthRef.current + deltaX));
      if (onKeyboardWidthChange) {
        onKeyboardWidthChange(newWidth);
      } else {
        setInternalKeyboardWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizingKeyboard(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingKeyboard, onKeyboardWidthChange]);

  // ==========================================
  // Interactive Loop Zone in Timeline Ruler
  // ==========================================
  interface LoopDragState {
    type: 'start' | 'end' | 'body' | 'create';
    startClientX: number;
    originStartMeasure: number;
    originEndMeasure: number;
    hasMoved: boolean;
  }
  const [loopDragState, setLoopDragState] = useState<LoopDragState | null>(null);

  const handleLoopMarkerMouseDown = (
    e: React.MouseEvent,
    type: 'start' | 'end' | 'body'
  ) => {
    e.stopPropagation();
    e.preventDefault();

    setLoopDragState({
      type,
      startClientX: e.clientX,
      originStartMeasure: loopRange.startMeasure,
      originEndMeasure: loopRange.endMeasure,
      hasMoved: false,
    });
  };

  // ==========================================
  // Playhead Dragging & Snapping in Ruler
  // ==========================================
  const rulerRef = useRef<HTMLDivElement>(null);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState<boolean>(false);

  const handlePlayheadMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    setIsDraggingPlayhead(true);

    if (rulerRef.current) {
      const rect = rulerRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const maxTick = effectiveMeasures * measureTicks;
      const rawTick = Math.max(0, Math.min(maxTick, clickX / pixelsPerTick));
      const shouldSnap = grid !== 'Off' && !e.altKey;
      const targetTick = shouldSnap ? snapTickToGrid(rawTick, grid, ppq) : Math.round(rawTick);
      onSeek(targetTick);
    }
  };

  const handleRulerMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const measureWidth = measureTicks * pixelsPerTick;
    const clickedMeasure = Math.max(1, Math.min(effectiveMeasures, Math.floor(clickX / measureWidth) + 1));

    // If click in top 11px strip of ruler, sweep out a loop range!
    if (clickY < 11) {
      e.preventDefault();
      onUpdateLoopRange({
        enabled: true,
        startMeasure: clickedMeasure,
        endMeasure: clickedMeasure,
      });

      // Move playhead to start of loop when loop is created/set
      onSeek((clickedMeasure - 1) * measureTicks);

      setLoopDragState({
        type: 'create',
        startClientX: e.clientX,
        originStartMeasure: clickedMeasure,
        originEndMeasure: clickedMeasure,
        hasMoved: false,
      });
      return;
    }

    // Otherwise ruler click seeks playhead AND begins dragging with snap!
    e.preventDefault();
    setIsDraggingPlayhead(true);
    const maxTick = effectiveMeasures * measureTicks;
    const rawTick = Math.max(0, Math.min(maxTick, clickX / pixelsPerTick));
    const shouldSnap = grid !== 'Off' && !e.altKey;
    const targetTick = shouldSnap ? snapTickToGrid(rawTick, grid, ppq) : Math.round(rawTick);
    onSeek(targetTick);
  };

  // Dragging playhead with window mousemove / mouseup (snaps to grid)
  useEffect(() => {
    if (!isDraggingPlayhead) return;

    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';

    const handleMouseMove = (e: MouseEvent) => {
      if (!rulerRef.current) return;
      const rect = rulerRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const maxTick = effectiveMeasures * measureTicks;
      const rawTick = Math.max(0, Math.min(maxTick, clickX / pixelsPerTick));

      const shouldSnap = grid !== 'Off' && !e.altKey;
      const targetTick = shouldSnap ? snapTickToGrid(rawTick, grid, ppq) : Math.round(rawTick);
      onSeek(targetTick);
    };

    const handleMouseUp = () => {
      setIsDraggingPlayhead(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = prevCursor;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingPlayhead, pixelsPerTick, grid, ppq, onSeek, effectiveMeasures, measureTicks]);

  useEffect(() => {
    if (!loopDragState) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - loopDragState.startClientX;
      const measureWidth = measureTicks * pixelsPerTick;
      const deltaMeasures = Math.round(deltaX / measureWidth);

      if (Math.abs(deltaX) > 3) {
        setLoopDragState((prev) => (prev ? { ...prev, hasMoved: true } : null));
      }

      if (loopDragState.type === 'start') {
        const newStart = Math.max(1, Math.min(loopDragState.originEndMeasure, loopDragState.originStartMeasure + deltaMeasures));
        onUpdateLoopRange({
          ...loopRange,
          enabled: true,
          startMeasure: newStart,
        });
      } else if (loopDragState.type === 'end') {
        const newEnd = Math.max(loopDragState.originStartMeasure, Math.min(effectiveMeasures, loopDragState.originEndMeasure + deltaMeasures));
        onUpdateLoopRange({
          ...loopRange,
          enabled: true,
          endMeasure: newEnd,
        });
      } else if (loopDragState.type === 'body') {
        const span = loopDragState.originEndMeasure - loopDragState.originStartMeasure;
        let newStart = loopDragState.originStartMeasure + deltaMeasures;
        let newEnd = newStart + span;

        if (newStart < 1) {
          newStart = 1;
          newEnd = 1 + span;
        } else if (newEnd > effectiveMeasures) {
          newEnd = effectiveMeasures;
          newStart = Math.max(1, effectiveMeasures - span);
        }

        onUpdateLoopRange({
          ...loopRange,
          startMeasure: newStart,
          endMeasure: newEnd,
        });
      } else if (loopDragState.type === 'create') {
        const deltaMeasuresCreate = Math.floor(deltaX / measureWidth);
        const currentMeasure = Math.max(1, Math.min(effectiveMeasures, loopDragState.originStartMeasure + deltaMeasuresCreate));
        const newStart = Math.min(loopDragState.originStartMeasure, currentMeasure);
        const newEnd = Math.max(loopDragState.originStartMeasure, currentMeasure);

        onUpdateLoopRange({
          enabled: true,
          startMeasure: newStart,
          endMeasure: newEnd,
        });
      }
    };

    const handleMouseUp = () => {
      setLoopDragState(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [loopDragState, measureTicks, pixelsPerTick, loopRange, effectiveMeasures, onUpdateLoopRange]);

  // ==========================================
  // Note Dragging & Resizing Initiation
  // ==========================================
  const handleNoteMouseDown = (
    e: React.MouseEvent,
    note: MidiNote,
    type: 'move' | 'resize-left' | 'resize-right'
  ) => {
    e.stopPropagation();
    e.preventDefault();

    // Eraser tool: delete on mousedown, then keep deleting any other note the pointer
    // is dragged over (see onMouseEnter on each note block below).
    if (toolMode === 'eraser') {
      isErasingRef.current = true;
      onDeleteNote?.(note.id);
      return;
    }

    // Razor tool: split the note in two at the exact point clicked, no drag/selection.
    if (toolMode === 'razor') {
      const noteRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const clickTickOffset = (e.clientX - noteRect.left) / pixelsPerTick;
      onSplitNote?.(note.id, note.startTick + clickTickOffset);
      return;
    }

    let targetIds: string[];
    if (selectedNoteIds.has(note.id)) {
      targetIds = Array.from<string>(selectedNoteIds);
    } else {
      if (!e.shiftKey) {
        onSelectNote(note.id, false);
        targetIds = [note.id];
      } else {
        onSelectNote(note.id, true);
        targetIds = [...Array.from<string>(selectedNoteIds), note.id];
      }
    }

    const snapshots = new Map<string, NoteSnapshot>();
    notesRef.current.forEach((n) => {
      if (targetIds.includes(n.id) || n.id === note.id) {
        snapshots.set(n.id, {
          id: n.id,
          startTick: n.startTick,
          durationTicks: n.durationTicks,
          pitch: n.pitch,
          velocity: n.velocity,
        });
      }
    });

    setDragAction({
      type,
      originClientX: e.clientX,
      originClientY: e.clientY,
      primaryNoteId: note.id,
      targetIds,
      snapshots,
      hasMoved: false,
    });
  };

  // Handle Grid Mouse Down (Add note, Marquee Select, or Pan view)
  const handleGridMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Middle click (button 1) or Space + Left click: Pan the editor view smoothly in X and Y
    if (e.button === 1 || (isSpacePressed && e.button === 0)) {
      e.preventDefault();
      setDragAction({
        type: 'pan',
        originClientX: e.clientX,
        originClientY: e.clientY,
        primaryNoteId: '',
        targetIds: [],
        snapshots: new Map(),
        hasMoved: false,
        startX: scrollContainerRef.current?.scrollLeft || 0,
        startY: scrollContainerRef.current?.scrollTop || 0,
      });
      return;
    }

    if (e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const clickedTick = clickX / pixelsPerTick;
    const pitchIndex = Math.floor(clickY / pitchRowHeight);
    const clickedPitch = maxPitch - pitchIndex;

    // Shift click or drag on background starts marquee selection
    if (e.shiftKey) {
      setDragAction({
        type: 'marquee',
        originClientX: e.clientX,
        originClientY: e.clientY,
        primaryNoteId: '',
        targetIds: [],
        snapshots: new Map(),
        hasMoved: false,
        startX: clickX,
        startY: clickY,
        currentX: clickX,
        currentY: clickY,
      });
      return;
    }

    // Normal click on empty cell: add note or clear selection (select tool only —
    // eraser/razor have nothing to do on empty space, just clear selection).
    if (toolMode !== 'select') {
      onClearSelection();
    } else if (selectedNoteIds.size > 0) {
      onClearSelection();
    } else {
      const activeSnapTicks = effectiveSnapGrid !== 'Off' ? getGridTicks(effectiveSnapGrid, ppq) : ppq / 4;
      const snappedTick = effectiveSnapGrid !== 'Off' ? snapTickToGrid(clickedTick, effectiveSnapGrid, ppq) : Math.round(clickedTick);
      onAddNote(clickedPitch, snappedTick, activeSnapTicks);
    }
  };

  // Global Mouse Move listeners for 60fps fluid dragging
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragAction) return;

      if (dragAction.type === 'pan') {
        if (scrollContainerRef.current) {
          const deltaX = e.clientX - dragAction.originClientX;
          const deltaY = e.clientY - dragAction.originClientY;
          scrollContainerRef.current.scrollLeft = (dragAction.startX ?? 0) - deltaX;
          scrollContainerRef.current.scrollTop = (dragAction.startY ?? 0) - deltaY;
        }
        return;
      }

      if (dragAction.type === 'marquee') {
        const rect = scrollContainerRef.current?.getBoundingClientRect();
        if (rect) {
          const headerH = measureChords ? 56 : 28;
          const currentX = e.clientX - rect.left + (scrollContainerRef.current?.scrollLeft || 0) - effectiveKeyboardWidth;
          const currentY = e.clientY - rect.top + (scrollContainerRef.current?.scrollTop || 0) - headerH;
          setDragAction((prev) => (prev ? { ...prev, currentX, currentY } : null));
        }
        return;
      }

      const totalDeltaX = e.clientX - dragAction.originClientX;
      const totalDeltaY = e.clientY - dragAction.originClientY;
      const rawDeltaTicks = totalDeltaX / pixelsPerTick;

      // Mark as moved once mouse exceeds small threshold
      if (!dragAction.hasMoved && (Math.abs(totalDeltaX) > 2 || Math.abs(totalDeltaY) > 2)) {
        setDragAction((prev) => (prev ? { ...prev, hasMoved: true } : null));
      }

      const isSnapActive = effectiveSnapGrid !== 'Off' && !e.altKey;
      const primarySnapshot = dragAction.snapshots.get(dragAction.primaryNoteId);
      if (!primarySnapshot) return;

      if (dragAction.type === 'move') {
        const deltaPitch = -Math.round(totalDeltaY / pitchRowHeight);

        let deltaTicksToApply: number;
        if (isSnapActive) {
          const rawTargetStart = primarySnapshot.startTick + rawDeltaTicks;
          const snappedTargetStart = Math.max(0, snapTickToGrid(rawTargetStart, effectiveSnapGrid, ppq));
          deltaTicksToApply = snappedTargetStart - primarySnapshot.startTick;
        } else {
          deltaTicksToApply = Math.round(rawDeltaTicks);
        }

        // Audition pitch on vertical movement
        const newPrimaryPitch = Math.max(minPitch, Math.min(maxPitch, primarySnapshot.pitch + deltaPitch));
        if (newPrimaryPitch !== lastAuditionPitchRef.current) {
          lastAuditionPitchRef.current = newPrimaryPitch;
          audioEngine.playNote(newPrimaryPitch, primarySnapshot.velocity, 0.15);
        }

        const updated = notesRef.current.map((n) => {
          const snap = dragAction.snapshots.get(n.id);
          if (!snap) return n;

          const newStart = Math.max(0, Math.round(snap.startTick + deltaTicksToApply));
          const newPitch = Math.max(minPitch, Math.min(maxPitch, snap.pitch + deltaPitch));

          return {
            ...n,
            startTick: newStart,
            rawStartTick: newStart,
            pitch: newPitch,
            manuallyEdited: true,
          };
        });

        if (onUpdateNotesLive) {
          onUpdateNotesLive(updated);
        } else if (onMoveNotes) {
          onMoveNotes(dragAction.targetIds, deltaTicksToApply, deltaPitch);
        }
      } else if (dragAction.type === 'resize-left') {
        const snapTicks = getGridTicks(isSnapActive ? effectiveSnapGrid : '1/16', ppq);
        const minDuration = isSnapActive ? Math.min(ppq / 4, snapTicks) : 15;

        let deltaTicksToApply: number;
        if (isSnapActive) {
          const rawTargetStart = primarySnapshot.startTick + rawDeltaTicks;
          const snappedTargetStart = Math.max(0, snapTickToGrid(rawTargetStart, effectiveSnapGrid, ppq));
          deltaTicksToApply = snappedTargetStart - primarySnapshot.startTick;
        } else {
          deltaTicksToApply = Math.round(rawDeltaTicks);
        }

        const updated = notesRef.current.map((n) => {
          const snap = dragAction.snapshots.get(n.id);
          if (!snap) return n;

          const origEnd = snap.startTick + snap.durationTicks;
          const proposedStart = Math.max(0, Math.round(snap.startTick + deltaTicksToApply));
          const clampedStart = Math.min(origEnd - minDuration, proposedStart);
          const newDuration = origEnd - clampedStart;

          return {
            ...n,
            startTick: clampedStart,
            rawStartTick: clampedStart,
            durationTicks: newDuration,
            rawDurationTicks: newDuration,
            manuallyEdited: true,
          };
        });

        if (onUpdateNotesLive) {
          onUpdateNotesLive(updated);
        } else if (onResizeNotes) {
          onResizeNotes(dragAction.targetIds, deltaTicksToApply);
        }
      } else if (dragAction.type === 'resize-right') {
        const snapTicks = getGridTicks(isSnapActive ? effectiveSnapGrid : '1/16', ppq);
        const minDuration = isSnapActive ? Math.min(ppq / 4, snapTicks) : 15;

        let deltaTicksToApply: number;
        if (isSnapActive) {
          const origEnd = primarySnapshot.startTick + primarySnapshot.durationTicks;
          const rawTargetEnd = origEnd + rawDeltaTicks;
          const snappedTargetEnd = snapTickToGrid(rawTargetEnd, effectiveSnapGrid, ppq);
          deltaTicksToApply = snappedTargetEnd - origEnd;
        } else {
          deltaTicksToApply = Math.round(rawDeltaTicks);
        }

        const updated = notesRef.current.map((n) => {
          const snap = dragAction.snapshots.get(n.id);
          if (!snap) return n;

          const newDuration = Math.max(minDuration, Math.round(snap.durationTicks + deltaTicksToApply));

          return {
            ...n,
            durationTicks: newDuration,
            rawDurationTicks: newDuration,
            manuallyEdited: true,
          };
        });

        if (onUpdateNotesLive) {
          onUpdateNotesLive(updated);
        } else if (onResizeNotes) {
          onResizeNotes(dragAction.targetIds, deltaTicksToApply);
        }
      }
    },
    [
      dragAction,
      pixelsPerTick,
      pitchRowHeight,
      effectiveSnapGrid,
      ppq,
      minPitch,
      maxPitch,
      audioEngine,
      onUpdateNotesLive,
      onMoveNotes,
      onResizeNotes,
      effectiveKeyboardWidth,
    ]
  );

  const handleMouseUp = useCallback(() => {
    if (!dragAction) return;

    if (dragAction.type === 'pan') {
      setDragAction(null);
      return;
    }

    if (
      dragAction.type === 'marquee' &&
      dragAction.startX !== undefined &&
      dragAction.startY !== undefined &&
      dragAction.currentX !== undefined &&
      dragAction.currentY !== undefined
    ) {
      const minX = Math.min(dragAction.startX, dragAction.currentX);
      const maxX = Math.max(dragAction.startX, dragAction.currentX);
      const minY = Math.min(dragAction.startY, dragAction.currentY);
      const maxY = Math.max(dragAction.startY, dragAction.currentY);

      const insideNotes = notesRef.current.filter((n) => {
        const noteLeft = n.startTick * pixelsPerTick;
        const noteRight = noteLeft + n.durationTicks * pixelsPerTick;
        const noteTop = (maxPitch - n.pitch) * pitchRowHeight;
        const noteBottom = noteTop + pitchRowHeight;

        return noteRight >= minX && noteLeft <= maxX && noteBottom >= minY && noteTop <= maxY;
      });

      insideNotes.forEach((n) => onSelectNote(n.id, true));
    } else {
      if (dragAction.hasMoved) {
        if (onCommitNotes) {
          onCommitNotes(notesRef.current);
        }
      } else if (dragAction.type === 'move') {
        const primary = dragAction.snapshots.get(dragAction.primaryNoteId);
        if (primary) {
          audioEngine.playNote(primary.pitch, primary.velocity, 0.25);
        }
      }
    }

    lastAuditionPitchRef.current = null;
    setDragAction(null);
  }, [
    dragAction,
    pixelsPerTick,
    maxPitch,
    pitchRowHeight,
    onSelectNote,
    onCommitNotes,
    audioEngine,
  ]);

  useEffect(() => {
    if (dragAction) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragAction, handleMouseMove, handleMouseUp]);

  // Active sounding pitches
  const activePitches = useMemo(() => {
    const active = new Set<number>();
    for (const note of notes) {
      if (currentTick >= note.startTick && currentTick <= note.startTick + note.durationTicks) {
        active.add(note.pitch);
      }
    }
    if (livePitches) {
      for (const pitch of livePitches) {
        active.add(pitch);
      }
    }
    return active;
  }, [notes, currentTick, livePitches]);

  // Grid vertical lines generator
  const verticalGridLines = useMemo(() => {
    const lines: React.ReactNode[] = [];
    const ticksPerCol = grid !== 'Off' ? gridTicks : beatTicks;
    const totalCols = Math.ceil(totalTicks / ticksPerCol);

    for (let i = 0; i <= totalCols; i++) {
      const tick = i * ticksPerCol;
      const x = tick * pixelsPerTick;
      const isMeasure = tick % measureTicks === 0;
      const isBeat = tick % beatTicks === 0;

      lines.push(
        <div
          key={`vline-${i}`}
          className={`absolute top-0 bottom-0 pointer-events-none ${
            isMeasure
              ? 'border-l border-[#3D3528] z-0'
              : isBeat
              ? 'border-l border-[#262119]'
              : 'border-l border-[#1E1B15]'
          }`}
          style={{ left: `${x}px` }}
        />
      );
    }
    return lines;
  }, [grid, gridTicks, beatTicks, totalTicks, measureTicks, pixelsPerTick]);

  // Horizontal pitch row stripes
  const horizontalRows = useMemo(() => {
    const rows: React.ReactNode[] = [];
    for (let p = maxPitch; p >= minPitch; p--) {
      const black = isBlackKey(p);
      const isC = p % 12 === 0;
      const isF = p % 12 === 5;
      const hasNaturalBoundary = isC || isF;
      const y = (maxPitch - p) * pitchRowHeight;
      const pitchName = getPitchName(p);

      rows.push(
        <div
          key={`row-${p}`}
          style={{
            top: `${y}px`,
            height: `${pitchRowHeight}px`,
          }}
          className={`absolute left-0 right-0 pointer-events-none flex items-center ${
            black
              ? 'bg-[#13110F]'
              : isC
              ? 'bg-[#221D16]'
              : 'bg-[#181614]'
          } ${
            hasNaturalBoundary
              ? 'border-b border-[#362F24]'
              : 'border-b border-[#231F1A]'
          }`}
        >
          {isC && (
            <span className="text-[9px] font-mono text-[#F5C767]/80 ml-1 select-none font-semibold">
              {pitchName}
            </span>
          )}
        </div>
      );
    }
    return rows;
  }, [minPitch, maxPitch, pitchRowHeight]);

  return (
    <div id="piano-roll-container" className="flex-1 flex flex-col bg-[#161412] overflow-hidden select-none relative">
      {/* Mini Controls Bar (Zoom, Snap, Fit, Note Height) */}
      <div className="flex items-center justify-between px-3 py-1 bg-[#181614] border-b border-[#2D2820] text-xs text-[#EBE6DB] flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-medium text-[#A89F91] font-mono">
            Piano Roll &bull; {notes.length} notes {selectedNoteIds.size > 0 && `(${selectedNoteIds.size} selected)`}
          </span>

          {/* Active Measure Detected Chord Display */}
          {(() => {
            const curMeasure = Math.floor(currentTick / measureTicks);
            const chords = measureChords?.get(curMeasure) || [];
            if (chords.length === 0) return null;
            const curChord =
              chords.find(
                (c) =>
                  currentTick >= (c.startTick ?? 0) &&
                  currentTick < ((c.startTick ?? 0) + (c.durationTicks ?? measureTicks))
              ) || chords[0];
            if (!curChord || !curChord.symbol) return null;
            return (
              <div
                id="piano-roll-current-chord-badge"
                className="flex items-center gap-1.5 px-2 py-0.5 bg-[#1C1A17] border border-[#3D362C] text-xs font-mono shadow-xs"
                title={`Bar ${curMeasure + 1} Chord: ${curChord.symbol}`}
              >
                <span className="text-[9px] uppercase tracking-wider text-[#A89F91]">Bar {curMeasure + 1}:</span>
                <span className="text-[#F5C767] font-bold text-xs">{curChord.symbol}</span>
                {curChord.pitches && curChord.pitches.length > 0 && (
                  <span className="text-[#A89F91] text-[10px] hidden md:inline">
                    ({curChord.pitches.map((p) => getPitchName(p)).join(' · ')})
                  </span>
                )}
              </div>
            );
          })()}

          {/* Edit Tool Selector — select (pointer), eraser, razor (split) */}
          <div
            id="piano-roll-tool-selector"
            className="flex items-center gap-0.5 bg-[#1C1A17] px-1 py-0.5 border border-[#3D362C]"
          >
            <button
              id="piano-roll-tool-select"
              onClick={() => setToolMode('select')}
              className={`flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                toolMode === 'select'
                  ? 'bg-[#F5C767]/15 text-[#F5C767] border border-[#F5C767]/50 shadow-xs'
                  : 'text-[#A89F91] hover:text-[#EBE6DB] border border-transparent'
              }`}
              title="Select tool [1] — click/drag to select, move, and resize notes"
            >
              <MousePointer2 className="w-3 h-3" />
              <span className="hidden sm:inline">Select</span>
            </button>
            <button
              id="piano-roll-tool-eraser"
              onClick={() => setToolMode('eraser')}
              className={`flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                toolMode === 'eraser'
                  ? 'bg-[#F5C767]/15 text-[#F5C767] border border-[#F5C767]/50 shadow-xs'
                  : 'text-[#A89F91] hover:text-[#EBE6DB] border border-transparent'
              }`}
              title="Eraser tool [2] — click or drag over notes to delete them"
            >
              <Eraser className="w-3 h-3" />
              <span className="hidden sm:inline">Eraser</span>
            </button>
            <button
              id="piano-roll-tool-razor"
              onClick={() => setToolMode('razor')}
              className={`flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                toolMode === 'razor'
                  ? 'bg-[#F5C767]/15 text-[#F5C767] border border-[#F5C767]/50 shadow-xs'
                  : 'text-[#A89F91] hover:text-[#EBE6DB] border border-transparent'
              }`}
              title="Razor tool [3] — click a note to split it in two at that point"
            >
              <Scissors className="w-3 h-3" />
              <span className="hidden sm:inline">Razor</span>
            </button>
          </div>

          {/* Snap Controls */}
          <div className="flex items-center gap-1.5 bg-[#1C1A17] px-2 py-0.5 border border-[#3D362C]">
            <button
              id="piano-roll-snap-toggle"
              onClick={() => setSnapEnabled((prev) => !prev)}
              className={`flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium transition-all ${
                snapEnabled
                  ? 'bg-[#F5C767]/15 text-[#F5C767] border border-[#F5C767]/50 shadow-xs'
                  : 'text-[#A89F91] hover:text-[#EBE6DB] border border-transparent'
              }`}
              title={`Snap to Grid: ${snapEnabled ? 'ON (Press S to toggle)' : 'OFF (Press S to toggle)'} • Hold Alt for free fluid drag`}
            >
              <Magnet className="w-3 h-3 text-current" />
              <span>Snap</span>
              <span
                className={`w-[24px] h-3.5 inline-flex items-center justify-center text-center font-mono text-[9px] font-bold ${
                  snapEnabled ? 'bg-[#F5C767] text-[#1B1B1B]' : 'bg-[#2D2820] text-[#A89F91]'
                }`}
              >
                {snapEnabled ? 'ON' : 'OFF'}
              </span>
            </button>

            {/* Snap Grid Resolution Select */}
            {snapEnabled && (
              <div className="flex items-center pl-1 border-l border-[#3D362C]">
                <select
                  id="piano-roll-snap-grid-select"
                  value={snapGrid}
                  onChange={(e) => setSnapGrid(e.target.value as QuantizeGrid | 'Auto')}
                  className="bg-transparent text-[#F5C767] text-[11px] focus:outline-none cursor-pointer py-0.5"
                  title="Snap Grid Resolution"
                >
                  <option value="Auto" className="bg-[#1C1A17] text-[#F5C767]">
                    Auto ({grid})
                  </option>
                  <option value="1/4" className="bg-[#1C1A17] text-[#F5C767]">
                    1/4 (Beat)
                  </option>
                  <option value="1/8" className="bg-[#1C1A17] text-[#F5C767]">
                    1/8
                  </option>
                  <option value="1/16" className="bg-[#1C1A17] text-[#F5C767]">
                    1/16
                  </option>
                  <option value="1/32" className="bg-[#1C1A17] text-[#F5C767]">
                    1/32
                  </option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Resizing & Zoom Controls: Horizontal (Time Width) and Vertical (Pitch Height) */}
        <div className="flex items-center gap-2">
          {/* Horizontal Time Zoom (Width) */}
          <div
            id="piano-roll-zoom-x-group"
            className="flex items-center gap-0.5 bg-[#1C1A17] px-1 py-0.5 border border-[#3D362C]"
            title="Horizontal zoom (time)"
          >
            <button
              onClick={() => setPixelsPerQuarter((prev) => Math.max(32, prev - 12))}
              className="p-1 text-[#A89F91] hover:text-[#F5C767] hover:bg-[#25211B] transition-colors"
              title="Zoom out horizontally"
            >
              <ZoomOut className="w-3 h-3" />
            </button>
            <div className="w-4 h-3 flex items-center justify-center text-[#A89F91]/70 font-mono text-[10px]">
              <span className="text-[11px] font-bold select-none">↔</span>
            </div>
            <button
              onClick={() => setPixelsPerQuarter((prev) => Math.min(180, prev + 12))}
              className="p-1 text-[#A89F91] hover:text-[#F5C767] hover:bg-[#25211B] transition-colors"
              title="Zoom in horizontally"
            >
              <ZoomIn className="w-3 h-3" />
            </button>
          </div>

          {/* Vertical Note Row Height Zoom */}
          <div
            id="piano-roll-zoom-y-group"
            className="flex items-center gap-0.5 bg-[#1C1A17] px-1 py-0.5 border border-[#3D362C]"
            title="Vertical zoom (pitch height)"
          >
            <button
              onClick={() => setPitchRowHeight((prev) => Math.max(12, prev - 2))}
              className="p-1 text-[#A89F91] hover:text-[#F5C767] hover:bg-[#25211B] transition-colors"
              title="Zoom out vertically"
            >
              <ZoomOut className="w-3 h-3" />
            </button>
            <div className="w-4 h-3 flex items-center justify-center text-[#A89F91]/70 font-mono text-[10px]">
              <span className="text-[11px] font-bold select-none">↕</span>
            </div>
            <button
              onClick={() => setPitchRowHeight((prev) => Math.min(32, prev + 2))}
              className="p-1 text-[#A89F91] hover:text-[#F5C767] hover:bg-[#25211B] transition-colors"
              title="Zoom in vertically"
            >
              <ZoomIn className="w-3 h-3" />
            </button>
          </div>

          {/* Center View on Notes (Focus) */}
          <button
            onClick={handleCenterView}
            className="flex items-center gap-1 px-1.5 py-0.5 bg-[#1C1A17] hover:bg-[#25211B] text-[#A89F91] hover:text-[#F5C767] border border-[#3D362C] text-[10px] font-mono transition-colors"
            title="Center view on notes (Focus)"
          >
            <Focus className="w-3 h-3 text-[#F5C767]" />
            <span>Focus</span>
          </button>

          {/* Auto-Scroll Follow Playhead Toggle */}
          {onToggleAutoScroll && (
            <button
              id="piano-roll-autoscroll-toggle-btn"
              onClick={onToggleAutoScroll}
              className={`flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-mono transition-colors ${
                autoScroll
                  ? 'bg-[#F5C767]/15 border-[#F5C767]/50 text-[#F5C767] shadow-xs font-semibold'
                  : 'bg-[#1C1A17] hover:bg-[#25211B] text-[#A89F91] hover:text-[#F5C767] border-[#3D362C]'
              }`}
              title={`Auto-scroll follow playhead [Shortcut: F]: ${autoScroll ? 'ENABLED' : 'DISABLED'}`}
            >
              <LocateFixed className={`w-3 h-3 ${autoScroll ? 'text-[#F5C767]' : 'text-[#A89F91]'}`} />
              <span className="hidden sm:inline">Follow</span>
            </button>
          )}

          {/* Toggle Velocity Lane */}
          {onToggleVelocityLane && (
            <button
              id="piano-roll-velocity-lane-toggle-btn"
              onClick={onToggleVelocityLane}
              className={`flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-mono transition-colors ${
                showVelocityLane
                  ? 'bg-[#F5C767]/15 border-[#F5C767]/50 text-[#F5C767] shadow-xs font-semibold'
                  : 'bg-[#1C1A17] hover:bg-[#25211B] text-[#A89F91] hover:text-[#F5C767] border-[#3D362C]'
              }`}
              title={`Show/Hide Velocity Lane [Shortcut: V]: ${showVelocityLane ? 'VISIBLE' : 'HIDDEN'}`}
            >
              <BarChart2 className={`w-3 h-3 ${showVelocityLane ? 'text-[#F5C767]' : 'text-[#A89F91]'}`} />
              <span className="hidden sm:inline">Velocity</span>
            </button>
          )}

          {/* Toggle Virtual Piano Keyboard */}
          {onToggleVirtualKeyboard && (
            <button
              id="piano-roll-virtual-keyboard-toggle-btn"
              onClick={onToggleVirtualKeyboard}
              className={`flex items-center gap-1 px-2 py-0.5 border text-[10px] font-mono transition-colors ${
                showVirtualKeyboard
                  ? 'bg-[#F5C767]/20 border-[#F5C767] text-[#F5C767] shadow-[0_0_8px_rgba(245,199,103,0.3)] font-semibold'
                  : 'bg-[#1C1A17] hover:bg-[#25211B] text-[#A89F91] hover:text-[#F5C767] border-[#3D362C]'
              }`}
              title={`Show/Hide Virtual Keyboard: ${showVirtualKeyboard ? 'VISIBLE' : 'HIDDEN'}`}
            >
              <Keyboard className={`w-3 h-3 ${showVirtualKeyboard ? 'text-[#F5C767]' : 'text-[#A89F91]'}`} />
              <span className="hidden sm:inline">Virtual Piano</span>
            </button>
          )}

          {/* Reset Zoom */}
          <button
            onClick={() => {
              setPixelsPerQuarter(64);
              setPitchRowHeight(18);
              if (onKeyboardWidthChange) onKeyboardWidthChange(76);
              else setInternalKeyboardWidth(76);
            }}
            className="p-1 hover:text-[#F5C767] hover:bg-[#25211B] text-[#A89F91] transition-colors rounded"
            title="Reset Zoom & Keyboard Width"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Main Scrollable Piano Roll View */}
      <div
        ref={scrollContainerRef}
        id="piano-roll-scroll-area"
        onScroll={(e) => {
          onScrollPositionChange?.(e.currentTarget.scrollLeft);
        }}
        onWheel={(e) => {
          e.stopPropagation();
          if (e.shiftKey && !e.ctrlKey && !e.metaKey && scrollContainerRef.current) {
            e.preventDefault();
            scrollContainerRef.current.scrollLeft += e.deltaY;
          }
        }}
        onMouseDown={(e) => {
          if (e.button === 1 || (isSpacePressed && e.button === 0)) {
            e.preventDefault();
            setDragAction({
              type: 'pan',
              originClientX: e.clientX,
              originClientY: e.clientY,
              primaryNoteId: '',
              targetIds: [],
              snapshots: new Map(),
              hasMoved: false,
              startX: scrollContainerRef.current?.scrollLeft || 0,
              startY: scrollContainerRef.current?.scrollTop || 0,
            });
          }
        }}
        className={`flex-1 overflow-auto relative flex overscroll-contain daw-scroll ${
          isSpacePressed ? (dragAction?.type === 'pan' ? 'cursor-grabbing' : 'cursor-grab') : ''
        }`}
      >
        {/* Left: Vertical Keyboard with Resizing Splitter */}
        <div className="relative sticky left-0 z-20 flex-shrink-0 flex flex-col">
          {/* Top Corner Header to align perfectly with the Timeline Ruler & Chord Track */}
          <div
            id="piano-keys-ruler-corner"
            style={{ width: `${effectiveKeyboardWidth}px`, height: measureChords ? '56px' : '28px' }}
            className="bg-[#1C1A17] border-b border-r-2 border-[#1B1B1B] sticky top-0 z-30 flex flex-col select-none shadow-xs flex-shrink-0"
          >
            <div className="h-7 border-b border-[#2D2820] flex items-center justify-between px-2 text-[10px] font-mono">
              <span className="font-bold text-[#F5C767] tracking-wider text-[9px]">BARS</span>
              <span className="text-[8px] text-[#A89F91] font-mono">KEYS</span>
            </div>
            {measureChords && (
              <div className="h-7 bg-[#161412] flex items-center justify-between px-2 text-[10px] font-mono border-b border-[#2D2820]">
                <span className="font-bold text-[9px] tracking-wider text-[#F5C767]">CHORDS</span>
                <span className="text-[7px] text-[#A89F91] font-mono uppercase">VOICING</span>
              </div>
            )}
          </div>

          <PianoKeys
            minPitch={minPitch}
            maxPitch={maxPitch}
            pitchRowHeight={pitchRowHeight}
            audioEngine={audioEngine}
            activePitches={activePitches}
            width={effectiveKeyboardWidth}
          />

          {/* Draggable Vertical Splitter for Piano Keys Width */}
          <div
            id="piano-keyboard-resizer"
            onMouseDown={handleKeyboardResizerMouseDown}
            onDoubleClick={() => {
              if (onKeyboardWidthChange) onKeyboardWidthChange(76);
              else setInternalKeyboardWidth(76);
            }}
            className={`absolute top-0 bottom-0 -right-1 w-2 cursor-col-resize z-30 flex items-center justify-center transition-colors group ${
              isResizingKeyboard ? 'bg-[#F5C767]/40' : 'bg-transparent hover:bg-[#F5C767]/25'
            }`}
            title="Drag left/right to resize Piano Keyboard width (Double-click to reset)"
          >
            <div className="w-[2px] h-8 bg-[#3D362C] group-hover:bg-[#F5C767] transition-colors flex items-center justify-center">
              <GripVertical className="w-2.5 h-2.5 text-[#A89F91] opacity-60 group-hover:opacity-100 group-hover:text-[#1B1B1B]" />
            </div>
          </div>
        </div>

        {/* Right: Timeline & Grid Container */}
        <div className="flex-1 relative flex flex-col" style={{ width: `${gridWidth}px` }}>
          {/* Top: Unified Sticky Timeline Header (Ruler + optional Chord Track) */}
          <div
            id="piano-roll-timeline-header"
            style={{ width: `${gridWidth}px`, height: measureChords ? '56px' : '28px' }}
            className="sticky top-0 z-20 flex flex-col flex-shrink-0 select-none shadow-xs"
          >
            {/* Interactive Timeline Ruler with Editable Loop Zone */}
            <div
              id="piano-roll-ruler"
              ref={rulerRef}
              onMouseDown={handleRulerMouseDown}
              style={{ width: `${gridWidth}px`, height: '28px' }}
              className="h-7 bg-[#1C1A17] border-b border-[#2D2820] cursor-pointer flex items-center shadow-xs select-none relative flex-shrink-0"
              title="Click bottom to seek playhead • Click & drag top strip to sweep new loop range"
            >
            {/* Measure Ticks & Labels */}
            {Array.from({ length: effectiveMeasures }).map((_, m) => {
              const measureX = m * measureTicks * pixelsPerTick;

              return (
                <div
                  key={`measure-ruler-${m}`}
                  style={{ left: `${measureX}px`, width: `${measureTicks * pixelsPerTick}px` }}
                  className="absolute top-0 bottom-0 border-l border-[#3D362C] flex items-center px-1 text-[10px] font-mono group hover:bg-[#F5C767]/5 pointer-events-none"
                >
                  <span className="font-bold text-[#F5C767] tracking-wider">{m + 1}</span>

                  {/* Beat subdivision ticks in ruler */}
                  {Array.from({ length: timeSignature.beats - 1 }).map((_, b) => (
                    <div
                      key={`beat-tick-${b}`}
                      style={{ left: `${(b + 1) * beatTicks * pixelsPerTick}px` }}
                      className="absolute top-2.5 bottom-0 border-l border-[#2D2820] w-px"
                    />
                  ))}
                </div>
              );
            })}

            {/* Interactive Editable Loop Range Strip */}
            {loopRange.enabled && (
              <div
                id="ruler-loop-strip"
                style={{
                  left: `${(loopRange.startMeasure - 1) * measureTicks * pixelsPerTick}px`,
                  width: `${(loopRange.endMeasure - loopRange.startMeasure + 1) * measureTicks * pixelsPerTick}px`,
                }}
                className="absolute top-0 bottom-0 bg-[#E5B558]/20 border-t-2 border-b border-[#F5C767] z-10 flex items-center justify-between"
              >
                {/* Left Loop Bracket Handle [L */}
                <div
                  id="ruler-loop-start-handle"
                  onMouseDown={(e) => handleLoopMarkerMouseDown(e, 'start')}
                  className="h-full w-4 cursor-ew-resize flex items-center justify-center bg-[#E5B558] hover:bg-[#F5C767] text-[#1B1B1B] font-bold text-[9px] rounded-r shadow-md z-30 transition-colors group"
                  title="Drag to resize Loop Start measure"
                >
                  <ChevronLeft className="w-2.5 h-2.5 group-hover:scale-125 transition-transform" />
                  <span className="text-[7px]">L</span>
                </div>

                {/* Loop Bar Center Body (Drag to shift loop or double-click to toggle) */}
                <div
                  id="ruler-loop-body"
                  onMouseDown={(e) => handleLoopMarkerMouseDown(e, 'body')}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    const nextEnabled = !loopRange.enabled;
                    onUpdateLoopRange({ ...loopRange, enabled: nextEnabled });
                    if (nextEnabled) {
                      onSeek((loopRange.startMeasure - 1) * measureTicks);
                    }
                  }}
                  className="flex-1 h-full cursor-grab active:cursor-grabbing flex items-center justify-center hover:bg-[#F5C767]/15 transition-colors px-1"
                  title={
                    loopRange.startMeasure === loopRange.endMeasure
                      ? `Loop: Bar ${loopRange.startMeasure} (1 bar) • Drag to move entire loop • Double-click to toggle`
                      : `Loop: Bars ${loopRange.startMeasure} to ${loopRange.endMeasure} (${loopRange.endMeasure - loopRange.startMeasure + 1} bars) • Drag to move entire loop • Double-click to toggle`
                  }
                >
                  <span className="text-[9px] font-mono font-bold text-[#F5C767] drop-shadow-sm select-none truncate">
                    {loopRange.startMeasure === loopRange.endMeasure
                      ? `Bar ${loopRange.startMeasure}`
                      : `Bars ${loopRange.startMeasure}–${loopRange.endMeasure}`}
                  </span>
                </div>

                {/* Right Loop Bracket Handle R] */}
                <div
                  id="ruler-loop-end-handle"
                  onMouseDown={(e) => handleLoopMarkerMouseDown(e, 'end')}
                  className="h-full w-4 cursor-ew-resize flex items-center justify-center bg-[#E5B558] hover:bg-[#F5C767] text-[#1B1B1B] font-bold text-[9px] rounded-l shadow-md z-30 transition-colors group"
                  title="Drag to resize Loop End measure"
                >
                  <span className="text-[7px]">R</span>
                  <ChevronRight className="w-2.5 h-2.5 group-hover:scale-125 transition-transform" />
                </div>
              </div>
            )}

            {/* Interactive Enlarged Playhead Handle in Ruler (Snaps to Grid) */}
            <div
              id="piano-roll-playhead-handle"
              onMouseDown={handlePlayheadMouseDown}
              style={{
                left: `${currentTick * pixelsPerTick}px`,
              }}
              className={`absolute top-0.5 z-40 -ml-[11px] w-[22px] h-[25px] select-none flex flex-col items-center cursor-grab active:cursor-grabbing transition-transform ${
                isDraggingPlayhead ? 'scale-110' : 'hover:scale-110'
              }`}
              title={`Playhead: Bar ${Math.floor(currentTick / measureTicks) + 1} (Snap: ${grid}) • Drag to seek along grid (Hold Alt for free scrub)`}
            >
              <svg
                width="22"
                height="25"
                viewBox="0 0 22 25"
                className="filter drop-shadow-[0_2px_6px_rgba(0,0,0,0.85)]"
              >
                {/* Pointer Badge Path */}
                <path
                  d="M3,2 L19,2 Q20.5,2 20.5,3.5 L20.5,15.5 Q20.5,16.5 19.8,17.2 L11.8,24.2 Q11,24.9 10.2,24.2 L2.2,17.2 Q1.5,16.5 1.5,15.5 L1.5,3.5 Q1.5,2 3,2 Z"
                  fill={isDraggingPlayhead ? '#FCD34D' : '#F5C767'}
                  stroke={isDraggingPlayhead ? '#FFFFFF' : '#B88218'}
                  strokeWidth="1.5"
                  className="transition-colors group-hover:fill-[#FCD34D] drop-shadow-[0_2px_8px_rgba(245,199,103,0.7)]"
                />
                {/* Tactical Grip Ridges */}
                <line x1="8" y1="6" x2="8" y2="13" stroke="#1B1B1B" strokeOpacity="0.75" strokeWidth="1.2" strokeLinecap="round" />
                <line x1="11" y1="5" x2="11" y2="14" stroke="#1B1B1B" strokeOpacity="0.9" strokeWidth="1.4" strokeLinecap="round" />
                <line x1="14" y1="6" x2="14" y2="13" stroke="#1B1B1B" strokeOpacity="0.75" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            </div>
          </div>

            {/* Dedicated Chord Track */}
            {measureChords && (
              <div style={{ width: `${gridWidth}px`, height: '28px' }} className="flex-shrink-0">
                <ChordTrack
                  measureChords={measureChords}
                  totalMeasures={effectiveMeasures}
                  timeSignature={timeSignature}
                  ppq={ppq}
                  pixelsPerTick={pixelsPerTick}
                  currentTick={currentTick}
                  audioEngine={audioEngine}
                  onUpdateChord={onUpdateChord}
                  height={28}
                />
              </div>
            )}
          </div>

          {/* Interactive Note Grid Canvas */}
          <div
            id="piano-roll-canvas"
            onMouseDown={handleGridMouseDown}
            style={{ width: `${gridWidth}px`, height: `${gridHeight}px` }}
            className="relative flex-shrink-0 cursor-crosshair bg-[#161412]"
          >
            {/* Background Grid Lines */}
            {horizontalRows}
            {verticalGridLines}

            {/* Loop Range Canvas Background Column Tint */}
            {loopRange.enabled && (
              <div
                className="absolute top-0 bottom-0 bg-[#F5C767]/[0.06] pointer-events-none border-x border-[#F5C767]/30 z-0"
                style={{
                  left: `${(loopRange.startMeasure - 1) * measureTicks * pixelsPerTick}px`,
                  width: `${(loopRange.endMeasure - loopRange.startMeasure + 1) * measureTicks * pixelsPerTick}px`,
                }}
              />
            )}

            {/* Rendered Notes */}
            {notes.map((note) => {
              const x = note.startTick * pixelsPerTick;
              const y = (maxPitch - note.pitch) * pitchRowHeight;
              const width = Math.max(10, note.durationTicks * pixelsPerTick);
              const isNoteSelected = selectedNoteIds.has(note.id);
              const vel = note.velocity;
              const pitchName = getPitchName(note.pitch);

              let noteGradientClass: string;
              let noteBorderClass: string;

              if (isNoteSelected) {
                noteGradientClass =
                  'bg-[#FCD34D] text-[#1B1B1B] font-bold ring-2 ring-[#FFF2C6] shadow-[0_0_10px_rgba(245,199,103,0.85)] z-20';
                noteBorderClass = 'border border-[#B88218]';
              } else if (vel >= 100) {
                noteGradientClass =
                  'bg-[#E5B558] text-[#1B1B1B] font-semibold shadow-xs';
                noteBorderClass = 'border border-[#B88218]/80';
              } else if (vel >= 70) {
                noteGradientClass =
                  'bg-[#D9A33E] text-[#1B1B1B] font-medium shadow-xs';
                noteBorderClass = 'border border-[#B88218]/60';
              } else {
                noteGradientClass =
                  'bg-[#C69230] text-[#1B1B1B]';
                noteBorderClass = 'border border-[#996D19]/60';
              }

              return (
                <div
                  key={note.id}
                  id={`note-block-${note.id}`}
                  onMouseDown={(e) => handleNoteMouseDown(e, note, 'move')}
                  onMouseEnter={() => {
                    if (toolMode === 'eraser' && isErasingRef.current) {
                      onDeleteNote?.(note.id);
                    }
                  }}
                  style={{
                    left: `${x}px`,
                    top: `${y + 1}px`,
                    width: `${width}px`,
                    height: `${pitchRowHeight - 2}px`,
                  }}
                  className={`group absolute rounded-[3px] text-[9px] font-mono select-none flex items-center px-1 overflow-hidden transition-none ${
                    toolMode === 'eraser' ? 'cursor-not-allowed' : toolMode === 'razor' ? 'cursor-col-resize' : 'cursor-grab active:cursor-grabbing'
                  } ${noteGradientClass} ${noteBorderClass}`}
                  title={`${pitchName} | Velocity: ${note.velocity} | Start: ${note.startTick} ticks | Dur: ${note.durationTicks}`}
                >
                  {/* Left Edge Resize Handle */}
                  <div
                    id={`note-resize-left-${note.id}`}
                    onMouseDown={(e) => handleNoteMouseDown(e, note, 'resize-left')}
                    className="absolute left-0 top-0 bottom-0 w-2.5 cursor-col-resize flex items-center justify-start pl-0.5 hover:bg-black/15 rounded-l-[2px] transition-colors z-20 group/left"
                    title="Drag left edge to resize note start (Hold Alt for free drag)"
                  >
                    <div className="w-[1.5px] h-2.5 bg-black/40 rounded-full opacity-75 group-hover/left:opacity-100 group-hover/left:bg-black" />
                  </div>

                  {/* Subtle top specular glossy line */}
                  <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-white/35 pointer-events-none rounded-t-[3px]" />

                  {/* Inner velocity meter line at bottom */}
                  <div
                    className="absolute bottom-0 left-0 h-[2px] bg-black/25 pointer-events-none rounded-b-[2px]"
                    style={{ width: `${Math.round((note.velocity / 127) * 100)}%` }}
                  />

                  <span className="truncate pointer-events-none leading-none font-semibold text-[9px] drop-shadow-xs pl-1.5">
                    {pitchName}
                  </span>

                  {/* Velocity readout if note is wide enough */}
                  {width >= 42 && (
                    <span className="text-[8px] opacity-75 font-mono pointer-events-none pr-1.5 ml-auto">
                      {note.velocity}
                    </span>
                  )}

                  {/* Right Edge Resize Handle */}
                  <div
                    id={`note-resize-right-${note.id}`}
                    onMouseDown={(e) => handleNoteMouseDown(e, note, 'resize-right')}
                    className="absolute right-0 top-0 bottom-0 w-2.5 cursor-col-resize flex items-center justify-end pr-0.5 hover:bg-black/15 rounded-r-[2px] transition-colors z-20 group/right"
                    title="Drag right edge to resize note duration (Hold Alt for free drag)"
                  >
                    <div className="w-[1.5px] h-2.5 bg-black/40 rounded-full opacity-75 group-hover/right:opacity-100 group-hover/right:bg-black" />
                  </div>
                </div>
              );
            })}

            {/* Drag Marquee Selection Box */}
            {dragAction?.type === 'marquee' &&
              dragAction.startX !== undefined &&
              dragAction.startY !== undefined &&
              dragAction.currentX !== undefined &&
              dragAction.currentY !== undefined && (
                <div
                  className="absolute border border-amber-400 bg-amber-500/20 pointer-events-none z-20"
                  style={{
                    left: `${Math.min(dragAction.startX, dragAction.currentX)}px`,
                    top: `${Math.min(dragAction.startY, dragAction.currentY)}px`,
                    width: `${Math.abs(dragAction.currentX - dragAction.startX)}px`,
                    height: `${Math.abs(dragAction.currentY - dragAction.startY)}px`,
                  }}
                />
              )}

            {/* Playhead Cursor Line across notes */}
            <div
              id="piano-roll-playhead"
              style={{ left: `${currentTick * pixelsPerTick}px` }}
              className="absolute top-0 bottom-0 w-[2px] bg-[#F5C767] z-30 pointer-events-none shadow-[0_0_10px_rgba(245,199,103,0.9)]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
